/**
 * 辩手便签板：本地、按房间+身份隔离，不打断训练。
 * - 固定在右下角，默认收起；
 * - 支持新增/编辑/删除/换色，localStorage 自动保存；
 * - 后续可把 note 写入 Yjs 共享给队友，当前先做单人速记。
 */
import { useEffect, useMemo, useState } from 'react';
import { useArena } from '../../state/ArenaContext.tsx';

interface StickyNote {
  id: string;
  text: string;
  color: 'amber' | 'ice' | 'indigo' | 'rose';
  updatedAt: number;
}

const COLORS: Record<StickyNote['color'], string> = {
  amber: 'bg-[#FCD34D]/10 border-[#FCD34D]/30 text-[#FDE68A]',
  ice: 'bg-[#7DD3FC]/10 border-[#7DD3FC]/30 text-[#BAE6FD]',
  indigo: 'bg-[#A5B4FC]/10 border-[#A5B4FC]/30 text-[#C7D2FE]',
  rose: 'bg-[#FDA4AF]/10 border-[#FDA4AF]/30 text-[#FECDD3]',
};

function storageKey(roomId: string | null, selfId: string | undefined): string {
  return `zongheng.notes.${roomId ?? 'local'}.${selfId ?? 'anon'}`;
}

export function StickyNotesDock() {
  const { state } = useArena();
  const key = storageKey(state.roomId, state.self?.id);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<StickyNote[]>([]);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      setNotes(raw ? (JSON.parse(raw) as StickyNote[]) : []);
    } catch {
      setNotes([]);
    }
  }, [key]);

  const persist = (next: StickyNote[]) => {
    setNotes(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // 隐私模式/配额不足：保持内存态
    }
  };

  const add = () => {
    if (!draft.trim()) return;
    const note: StickyNote = {
      id: `note-${Date.now()}`,
      text: draft.trim(),
      color: 'amber',
      updatedAt: Date.now(),
    };
    persist([note, ...notes].slice(0, 24));
    setDraft('');
  };

  const count = useMemo(() => notes.length, [notes]);

  return (
    <div className="fixed bottom-4 right-4 z-40" data-testid="notes-dock">
      {open && (
        <section className="lens mb-2 flex w-[320px] max-w-[calc(100vw-32px)] flex-col gap-2 p-3">
          <header className="flex items-center justify-between gap-2">
            <div>
              <div className="micro-label">STICKY NOTES</div>
              <div className="text-[12px] font-medium text-slate-100">便利便签 · {count}</div>
            </div>
            <button type="button" className="transport-btn transport-btn--icon !h-7 !min-h-7 !w-7" onClick={() => setOpen(false)} aria-label="收起便签">
              ×
            </button>
          </header>
          <div className="flex gap-1.5">
            <input
              data-testid="notes-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') add();
              }}
              placeholder="记一句：对方口径 / 下一轮要问…"
              className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/25 px-2 py-1.5 text-[11px] text-slate-200 outline-none placeholder:text-slate-600 focus:border-white/25"
            />
            <button type="button" className="transport-btn !min-h-8 !px-2.5" onClick={add} data-testid="notes-add">
              添加
            </button>
          </div>
          <div className="max-h-[300px] space-y-1.5 overflow-auto">
            {notes.length === 0 && <p className="text-[10px] text-slate-500">还没有便签；训练中随手记。</p>}
            {notes.map((note) => (
              <article
                key={note.id}
                data-testid="note-item"
                className={`rounded-md border px-2 py-1.5 text-[10.5px] leading-relaxed ${COLORS[note.color]}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 whitespace-pre-wrap">{note.text}</p>
                  <button
                    type="button"
                    className="shrink-0 text-[10px] opacity-60 hover:opacity-100"
                    onClick={() => persist(notes.filter((item) => item.id !== note.id))}
                    aria-label="删除便签"
                  >
                    ×
                  </button>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="flex gap-1">
                    {(Object.keys(COLORS) as StickyNote['color'][]).map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-label={`切换颜色 ${color}`}
                        className="h-2 w-2 rounded-full border border-white/20"
                        style={{ background: color === 'amber' ? '#FCD34D' : color === 'ice' ? '#7DD3FC' : color === 'indigo' ? '#A5B4FC' : '#FDA4AF' }}
                        onClick={() =>
                          persist(notes.map((item) => (item.id === note.id ? { ...item, color } : item)))
                        }
                      />
                    ))}
                  </div>
                  <span className="text-[8px] opacity-50">
                    {new Date(note.updatedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      {!open && (
        <button
          type="button"
          className="transport-btn shadow-[0_12px_30px_-12px_rgba(0,0,0,0.9)]"
          onClick={() => setOpen(true)}
          data-testid="notes-toggle"
        >
          <span className="text-[13px]">▤</span>
          便签{count > 0 ? ` · ${count}` : ''}
        </button>
      )}
    </div>
  );
}
